/**
 * Plain-English activity sentences (t122u). Task activity rows store raw change records
 * ({ status_id: { from, to } }, 'comment.added' …); this turns them into en-GB phrases with names resolved from the
 * server's `refs` (only what the viewer may see). Never shows field names, ids or dotted keys: unknown fields get a
 * friendly label (and a dev warning), unknown events a neutral "made a change".
 */
import { formatGBP } from '@tradeoutlet/shared';
import type { ActivityRefs } from '@tradeoutlet/shared';
import { DateTime } from 'luxon';
import { describeRecurrence } from '@/features/projects/RecurrenceEditor';
import { minutes } from './format';

export interface ActivityRow { id: number | string; actor_id: string | null; action: string; changes: Record<string, unknown> | null; created_at: string }
/** One phrase of a sentence: "changed the status from To do to In progress". `comment_id` makes it a link to the comment. */
export interface Phrase { text: string; comment_id?: string; quote?: string }
export interface ActivityEntry { key: string; actor_id: string | null; at: string; first_at: string; phrases: Phrase[]; rows: ActivityRow[] }

export const EMPTY_REFS: ActivityRefs = { users: {}, teams: {}, statuses: {}, milestones: {}, locations: {}, tasks: {}, projects: {}, comments: {} };

/** Friendly names for fields that have no bespoke sentence. */
const FIELD_LABELS: Record<string, string> = {
  title: 'title', description: 'description', status_id: 'status', priority: 'priority', milestone_id: 'milestone', start_date: 'start date',
  due_date: 'due date', due_time: 'due time', estimate_minutes: 'estimate', parent_task_id: 'parent task', recurrence: 'repeat', location_id: 'building',
  area_id: 'area', budget_pence: 'budget', reminder_minutes: 'reminder', visibility: 'visibility', assignees: 'assignees', teams: 'teams',
  custom_fields: 'custom fields', watchers: 'watchers',
};
const warned = new Set<string>();
function devWarn(what: string) {
  if (import.meta.env?.DEV && !warned.has(what)) { warned.add(what); console.warn(`activityFormat: no wording for ${what}; using a generic label`); }
}
const humanise = (k: string) => k.replace(/_(id|ids|minutes|pence)$/, '').replace(/_/g, ' ');
export const fieldLabel = (k: string) => FIELD_LABELS[k] ?? (devWarn(`field "${k}"`), humanise(k));

const PRIORITY: Record<string, string> = { none: 'No priority', low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };
const day = (v: unknown) => { const d = DateTime.fromISO(String(v)); return d.isValid ? d.setLocale('en-GB').toFormat('ccc d LLL') : String(v); };
const quote = (s: string) => `‘${s}’`;
const list = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const ids = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const pair = (v: unknown) => (v && typeof v === 'object' && 'to' in (v as object) ? v as { from: unknown; to: unknown } : { from: undefined, to: v });

/** "from X to Y" / "to Y" / "removed X" around a value formatter. */
function change(label: string, v: unknown, show: (x: unknown) => string | null, verbs = { set: 'set the', cleared: 'cleared the' }): string {
  const { from, to } = pair(v);
  const a = from == null || from === '' ? null : show(from);
  const b = to == null || to === '' ? null : show(to);
  if (a && b) return `changed the ${label} from ${a} to ${b}`;
  if (b) return `${verbs.set} ${label} to ${b}`;
  if (a) return `${verbs.cleared} ${label} (was ${a})`;
  return `changed the ${label}`;
}

export function describeField(k: string, v: unknown, r: ActivityRefs): Phrase[] {
  const name = {
    user: (id: string) => r.users[id] ?? 'someone', team: (id: string) => r.teams[id] ?? 'a team',
    task: (id: unknown) => { const t = r.tasks[String(id)]; return t ? quote(t.title) : 'a task you can’t see'; },
  };
  switch (k) {
    case 'title': { const { from, to } = pair(v); return [{ text: from ? `renamed the task from ${quote(String(from))} to ${quote(String(to))}` : `set the title to ${quote(String(to))}` }]; }
    case 'description': return [{ text: pair(v).to ? 'updated the description' : 'cleared the description' }];
    case 'status_id': return [{ text: change('status', v, x => r.statuses[String(x)] ?? 'another status') }];
    case 'priority': return [{ text: change('priority', v, x => PRIORITY[String(x)] ?? String(x)) }];
    case 'milestone_id': return [{ text: change('milestone', v, x => r.milestones[String(x)] ?? 'a milestone', { set: 'set the', cleared: 'removed the' }) }];
    case 'start_date': return [{ text: change('start date', v, day) }];
    case 'due_date': return [{ text: change('due date', v, day) }];
    case 'due_time': return [{ text: change('due time', v, x => String(x).slice(0, 5)) }];
    case 'estimate_minutes': return [{ text: change('estimate', v, x => minutes(Number(x))) }];
    case 'budget_pence': return [{ text: change('budget', v, x => formatGBP(Number(x))) }];
    case 'recurrence': { const { to } = pair(v); return [{ text: to ? `set it to repeat ${describeRecurrence(to as never).replace(/^./, ch => ch.toLowerCase())}` : 'stopped it repeating' }]; }
    case 'parent_task_id': { const { from, to } = pair(v); return [{ text: to ? `made it a subtask of ${name.task(to)}` : `made it a standalone task (was under ${name.task(from)})` }]; }
    case 'location_id': {
      const { from, to } = pair(v);
      const loc = (x: unknown) => { const l = r.locations[String(x)]; return l ? `${l.name}${l.archived ? ' (archived)' : ''}` : 'a building'; };
      return [{ text: to ? `moved it to ${loc(to)}${from ? ` (from ${loc(from)})` : ''}` : `removed the building (was ${loc(from)})` }];
    }
    case 'assignees': case 'assignee_ids': {
      const x = (Array.isArray(v) ? { added: v, removed: [] } : v ?? {}) as { added?: unknown; removed?: unknown };
      const out: Phrase[] = [];
      if (ids(x.added).length) out.push({ text: `assigned ${list(ids(x.added).map(name.user))}` });
      if (ids(x.removed).length) out.push({ text: `unassigned ${list(ids(x.removed).map(name.user))}` });
      return out;
    }
    case 'teams': case 'team_ids': {
      const x = (Array.isArray(v) ? { added: v, removed: [] } : v ?? {}) as { added?: unknown; removed?: unknown };
      const out: Phrase[] = [];
      if (ids(x.added).length) out.push({ text: `added ${list(ids(x.added).map(name.team))}` });
      if (ids(x.removed).length) out.push({ text: `removed ${list(ids(x.removed).map(name.team))}` });
      return out;
    }
    case 'custom_fields': return Object.entries((v ?? {}) as Record<string, unknown>).map(([f, val]) => ({ text: val == null || val === '' ? `cleared ${f}` : `set ${f} to ${Array.isArray(val) ? val.join(', ') : String(val)}` }));
    default: return [{ text: `changed the ${fieldLabel(k)}` }];
  }
}

/** The phrases for one raw row. */
export function describeRow(row: ActivityRow, r: ActivityRefs = EMPTY_REFS): Phrase[] {
  const c = row.changes ?? {};
  const task = (id: unknown) => { const t = r.tasks[String(id)]; return t ? quote(t.title) : 'a task you can’t see'; };
  const fields = (skip: string[] = []) => Object.entries(c).filter(([k]) => !skip.includes(k)).flatMap(([k, v]) => describeField(k, v, r));
  switch (row.action) {
    case 'task.created': {
      if (c.recurring_from) return [{ text: `created this repeat of ${task(c.recurring_from)}${c.due_date ? `, due ${day(c.due_date)}` : ''}` }];
      const extra = [...describeField('assignee_ids', c.assignee_ids, r), ...describeField('team_ids', c.team_ids, r)];
      return [{ text: 'created the task' }, ...extra];
    }
    case 'task.completed': return [{ text: 'completed the task' }, ...fields(['status_id', 'completed_at'])];
    case 'task.updated': return fields();
    case 'task.deleted': return [{ text: 'deleted the task' }];
    case 'task.moved': {
      const p = (id: unknown) => (id ? r.projects[String(id)] ?? 'a project you can’t see' : 'No project');
      return [{ text: `moved the task to ${p(c.to_project_id)}${c.from_project_id !== undefined ? ` (from ${p(c.from_project_id)})` : ''}` }];
    }
    case 'comment.added': {
      const id = String(c.comment_id ?? '');
      const first = r.comments[id];
      return [first ? { text: 'commented:', quote: first, comment_id: id } : { text: 'added a comment (since deleted)' }];
    }
    // t258u: the comment itself is gone or changed, so these just say what happened.
    case 'comment.edited': return [{ text: 'edited a comment' }];
    case 'comment.deleted': return [{ text: c.author_id ? 'removed a comment' : 'deleted a comment' }];
    case 'subtask.added': return [{ text: `added subtask ${r.tasks[String(c.subtask_id)] ? quote(r.tasks[String(c.subtask_id)].title) : '(one you can’t see)'}` }];
    case 'dependency.added': return [{ text: `marked it as waiting on ${task(c.depends_on_task_id)}` }];
    case 'dependency.removed': return [{ text: `removed the wait on ${task(c.depends_on_task_id)}` }];
    case 'link.added': return [{ text: c.message_id ? 'linked a chat message' : 'linked a conversation' }];
    case 'attachment.added': return [{ text: c.file_name ? `attached ${quote(String(c.file_name))}` : 'attached a file' }];
    default: devWarn(`event "${row.action}"`); return [{ text: 'made a change' }];
  }
}

/**
 * Newest-first entries; consecutive rows by the same person within `windowMs` (default 2 minutes) merge into one
 * entry ("changed the status …, set the due date … and assigned Emma"). Comments stay on their own line.
 */
export function groupActivity(rows: ActivityRow[], refs: ActivityRefs = EMPTY_REFS, windowMs = 120_000): ActivityEntry[] {
  const out: ActivityEntry[] = [];
  for (const row of rows) {
    const phrases = describeRow(row, refs);
    if (!phrases.length) continue;
    const prev = out[out.length - 1];
    const isComment = row.action === 'comment.added';
    if (prev && !isComment && !prev.phrases.some(p => p.comment_id) && prev.actor_id === row.actor_id
      && Math.abs(Date.parse(prev.first_at) - Date.parse(row.created_at)) <= windowMs) {
      prev.phrases.push(...phrases.filter(p => !prev.phrases.some(q => q.text === p.text)));
      prev.first_at = row.created_at; prev.rows.push(row);
      continue;
    }
    out.push({ key: String(row.id), actor_id: row.actor_id, at: row.created_at, first_at: row.created_at, phrases, rows: [row] });
  }
  // Rows arrive newest first, so within a merged entry the phrases are newest first too; read them oldest first.
  for (const e of out) if (e.rows.length > 1) e.phrases = e.rows.slice().reverse().flatMap(r => describeRow(r, refs)).filter((p, i, a) => a.findIndex(q => q.text === p.text) === i);
  return out;
}

/** Joins phrases into one sentence body: "a, b and c". */
export const sentence = (ps: Phrase[]) => list(ps.map(p => p.text));
